import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getProductsForReorder, reorderProducts, getCategories } from '../lib/api/productsApi';
import { toast } from 'react-toastify';
import {
    RiLayoutGridLine,
    RiListUnordered,
    RiSearch2Line,
    RiRefreshLine,
    RiSave3Line,
    RiDragMove2Fill,
    RiArrowUpLine,
    RiArrowDownLine,
    RiPushpin2Line,
    RiShoppingBag3Line,
    RiImageLine,
    RiFilter3Line,
    RiInformationLine,
    RiArrowGoBackLine,
    RiPriceTag3Line,
    RiCloseLine
} from "react-icons/ri";

const ReorderProducts = () => {
    const [products, setProducts] = useState([]);
    const [originalProducts, setOriginalProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Filters & View State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [isDirty, setIsDirty] = useState(false);

    // Drag and drop states
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Direct rank input temp state
    const [rankInputValues, setRankInputValues] = useState({});

    // Fetch brand products and categories
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [productRes, catRes] = await Promise.all([
                getProductsForReorder(),
                getCategories()
            ]);

            if (productRes?.success && Array.isArray(productRes.data)) {
                setProducts(productRes.data);
                setOriginalProducts(productRes.data);
                setIsDirty(false);
            } else {
                setProducts([]);
                setOriginalProducts([]);
            }

            const catList = catRes?.data || catRes?.categories || (Array.isArray(catRes) ? catRes : []);
            if (Array.isArray(catList)) {
                setCategories(catList);
            }
        } catch (error) {
            console.error('Failed to load products for reordering:', error);
            toast.error('Failed to load products. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Compute unique product types present in catalog
    const availableTypes = useMemo(() => {
        const typesSet = new Set();
        products.forEach((p) => {
            if (p.type) {
                typesSet.add(p.type.toLowerCase());
            }
        });
        ['variable', 'single', 'combo', 'makecombo', 'customproduct'].forEach((t) => typesSet.add(t));
        return Array.from(typesSet);
    }, [products]);

    // Filtered view of products (Search, Category, Product Type)
    const filteredProducts = useMemo(() => {
        return products.filter((p) => {
            // 1. Search term match
            const matchSearch = searchTerm
                ? (p.name && p.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                  (p.productID && p.productID.toLowerCase().includes(searchTerm.toLowerCase())) ||
                  (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase()))
                : true;

            // 2. Product type match
            const matchType = selectedType
                ? (p.type && p.type.toLowerCase() === selectedType.toLowerCase()) ||
                  (!p.type && selectedType === 'single')
                : true;

            // 3. Category match
            let matchCategory = true;
            if (selectedCategory) {
                let cats = p.categories;
                if (typeof cats === 'string') {
                    try {
                        cats = JSON.parse(cats);
                    } catch {
                        cats = [cats];
                    }
                }

                const selCatObj = categories.find((cat) => String(cat.categoryID) === String(selectedCategory) || String(cat.categoryName) === String(selectedCategory));
                const selCatID = String(selectedCategory).toLowerCase();
                const selCatName = selCatObj ? selCatObj.categoryName.toLowerCase() : selCatID;

                if (Array.isArray(cats)) {
                    matchCategory = cats.some((c) => {
                        if (typeof c === 'object' && c !== null) {
                            const cID = String(c.categoryID || c.id || c.category_id || '').toLowerCase();
                            const cName = String(c.categoryName || c.name || '').toLowerCase();
                            return cID === selCatID || cName === selCatName || (cID && cID === selCatName);
                        }
                        const strC = String(c).toLowerCase();
                        return strC === selCatID || strC === selCatName;
                    });
                } else if (typeof cats === 'object' && cats !== null) {
                    const cID = String(cats.categoryID || cats.id || cats.category_id || '').toLowerCase();
                    const cName = String(cats.categoryName || cats.name || '').toLowerCase();
                    matchCategory = cID === selCatID || cName === selCatName || (cID && cID === selCatName);
                } else if (cats != null) {
                    const strC = String(cats).toLowerCase();
                    matchCategory = strC === selCatID || strC === selCatName;
                } else {
                    matchCategory = false;
                }
            }

            return matchSearch && matchType && matchCategory;
        });
    }, [products, searchTerm, selectedType, selectedCategory, categories]);

    // Reorder action: move item from sourceIndex to targetIndex in the master list
    const moveItemInMasterList = useCallback((fromProductID, toMasterIndex) => {
        setProducts((prev) => {
            const currentIdx = prev.findIndex((p) => p.productID === fromProductID);
            if (currentIdx === -1) return prev;

            const targetIdx = Math.max(0, Math.min(toMasterIndex, prev.length - 1));
            if (currentIdx === targetIdx) return prev;

            const updated = [...prev];
            const [movedItem] = updated.splice(currentIdx, 1);
            updated.splice(targetIdx, 0, movedItem);

            setIsDirty(true);
            return updated;
        });
    }, []);

    // Drag handlers
    const handleDragStart = (e, productID) => {
        const idx = products.findIndex((p) => p.productID === productID);
        setDraggedIndex(idx);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', productID);
    };

    const handleDragOver = (e, productID) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const idx = products.findIndex((p) => p.productID === productID);
        if (dragOverIndex !== idx) {
            setDragOverIndex(idx);
        }
    };

    const handleDrop = (e, targetProductID) => {
        e.preventDefault();
        const sourceProductID = e.dataTransfer.getData('text/plain');
        if (!sourceProductID || sourceProductID === targetProductID) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const targetMasterIdx = products.findIndex((p) => p.productID === targetProductID);
        if (targetMasterIdx !== -1) {
            moveItemInMasterList(sourceProductID, targetMasterIdx);
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Quick move handlers (supports both filtered & unfiltered contexts)
    const handleMoveToTop = (productID) => {
        moveItemInMasterList(productID, 0);
        toast.info('Moved product to top (#1)');
    };

    const handleMoveUp = (productID) => {
        if (searchTerm || selectedCategory || selectedType) {
            const filterIdx = filteredProducts.findIndex((p) => p.productID === productID);
            if (filterIdx > 0) {
                const prevFilteredProduct = filteredProducts[filterIdx - 1];
                const targetMasterIdx = products.findIndex((p) => p.productID === prevFilteredProduct.productID);
                moveItemInMasterList(productID, targetMasterIdx);
            }
        } else {
            const currentIdx = products.findIndex((p) => p.productID === productID);
            if (currentIdx > 0) {
                moveItemInMasterList(productID, currentIdx - 1);
            }
        }
    };

    const handleMoveDown = (productID) => {
        if (searchTerm || selectedCategory || selectedType) {
            const filterIdx = filteredProducts.findIndex((p) => p.productID === productID);
            if (filterIdx < filteredProducts.length - 1) {
                const nextFilteredProduct = filteredProducts[filterIdx + 1];
                const targetMasterIdx = products.findIndex((p) => p.productID === nextFilteredProduct.productID);
                moveItemInMasterList(productID, targetMasterIdx);
            }
        } else {
            const currentIdx = products.findIndex((p) => p.productID === productID);
            if (currentIdx < products.length - 1) {
                moveItemInMasterList(productID, currentIdx + 1);
            }
        }
    };

    // Rank input change & jump
    const handleRankInputChange = (productID, val) => {
        setRankInputValues((prev) => ({ ...prev, [productID]: val }));
    };

    const handleRankInputCommit = (productID) => {
        const val = rankInputValues[productID];
        if (val === undefined || val === '') return;
        const targetNum = parseInt(val, 10);
        if (isNaN(targetNum) || targetNum < 1) {
            toast.warn('Please enter a valid rank number (1 or greater)');
            return;
        }

        if (searchTerm || selectedCategory || selectedType) {
            const targetFilterIdx = Math.min(targetNum - 1, filteredProducts.length - 1);
            const targetProduct = filteredProducts[targetFilterIdx];
            if (targetProduct) {
                const targetMasterIdx = products.findIndex((p) => p.productID === targetProduct.productID);
                moveItemInMasterList(productID, targetMasterIdx);
            }
        } else {
            const targetIdx = targetNum - 1;
            moveItemInMasterList(productID, targetIdx);
        }

        setRankInputValues((prev) => {
            const next = { ...prev };
            delete next[productID];
            return next;
        });
    };

    // Reset arrangement back to original or chronological
    const handleResetToOriginal = () => {
        setProducts([...originalProducts]);
        setIsDirty(false);
        toast.info('Arrangement restored to previous saved state.');
    };

    const handleResetToNewest = () => {
        const sortedByDate = [...products].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setProducts(sortedByDate);
        setIsDirty(true);
        toast.info('Products reordered by newest upload date.');
    };

    // Save arrangement to backend
    const handleSaveOrder = async () => {
        if (!isDirty) {
            toast.info('No changes to save.');
            return;
        }

        setSaving(true);
        try {
            const payload = products.map((p, idx) => ({
                productID: p.productID,
                displayOrder: idx + 1
            }));

            const res = await reorderProducts(payload);
            if (res?.success) {
                toast.success('🎉 Product arrangement saved and live on your brand page!');
                setOriginalProducts([...products]);
                setIsDirty(false);
            } else {
                toast.error(res?.message || 'Failed to save product arrangement.');
            }
        } catch (error) {
            console.error('Error saving brand product order:', error);
            toast.error(error.response?.data?.message || 'Failed to save product order.');
        } finally {
            setSaving(false);
        }
    };

    // Helper for product image URL
    const getProductImageUrl = (featuredImage) => {
        if (!featuredImage) return null;

        let data = featuredImage;
        if (typeof data === 'string') {
            const trimmed = data.trim();
            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                try {
                    data = JSON.parse(trimmed);
                } catch {
                    return trimmed;
                }
            } else {
                return trimmed;
            }
        }

        if (Array.isArray(data)) {
            if (data.length === 0) return null;
            const first = data[0];
            if (typeof first === 'string') return first;
            if (typeof first === 'object' && first !== null) {
                return first.imgUrl || first.url || first.thumbnail || first.image || null;
            }
            return null;
        }

        if (typeof data === 'object' && data !== null) {
            return data.imgUrl || data.url || data.thumbnail || data.image || null;
        }

        return null;
    };

    // Helper for type badge colors
    const getTypeBadgeColor = (type) => {
        switch (type?.toLowerCase()) {
            case 'variable':
                return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
            case 'combo':
                return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
            case 'makecombo':
            case 'mcombo':
                return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
            case 'customproduct':
                return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
            case 'single':
            case 'simple':
            default:
                return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
        }
    };

    const hasActiveFilters = Boolean(searchTerm || selectedCategory || selectedType);

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] p-4 md:p-6 space-y-6">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header Card */}
                <div className="card p-6 bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-sm">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3 mb-1">
                                <div className="p-2.5 bg-[var(--accent)] text-[var(--accent-contrast)] rounded-xl flex items-center justify-center">
                                    <RiLayoutGridLine className="w-6 h-6" />
                                </div>
                                <div>
                                    <h1 className="text-xl md:text-2xl font-bold text-[var(--text)]">
                                        Arrange Products
                                    </h1>
                                    <p className="text-xs md:text-sm text-[var(--muted)]">
                                        Drag & drop or change ranks to organize how your products appear on your brand storefront page.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Top Action Buttons */}
                        <div className="flex flex-wrap items-center gap-2.5">
                            <button
                                type="button"
                                onClick={handleResetToNewest}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs md:text-sm font-medium bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] hover:border-[var(--accent)] hover:text-[var(--accent)] rounded-xl transition-all shadow-sm"
                                title="Sort products by newest upload date"
                            >
                                <RiArrowGoBackLine className="w-4 h-4" />
                                Order by Newest
                            </button>

                            {isDirty && (
                                <button
                                    type="button"
                                    onClick={handleResetToOriginal}
                                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs md:text-sm font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 rounded-xl transition-all shadow-sm"
                                >
                                    <RiRefreshLine className="w-4 h-4" />
                                    Reset Changes
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={handleSaveOrder}
                                disabled={saving || !isDirty}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs md:text-sm font-semibold rounded-xl shadow-sm transition-all ${
                                    isDirty
                                        ? 'bg-[var(--accent)] text-[var(--accent-contrast)] hover:opacity-90 active:scale-95 cursor-pointer shadow-md'
                                        : 'bg-[var(--border)] text-[var(--muted)] opacity-60 cursor-not-allowed'
                                }`}
                            >
                                {saving ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <RiSave3Line className="w-4 h-4" />
                                        <span>Save Arrangement</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Unsaved Changes Banner */}
                    {isDirty && (
                        <div className="mt-4 flex items-center justify-between p-3 bg-purple-500/10 border border-purple-500/30 rounded-xl text-purple-400 text-xs md:text-sm">
                            <div className="flex items-center gap-2">
                                <span className="flex h-2.5 w-2.5 rounded-full bg-purple-500 animate-pulse" />
                                <span className="font-semibold text-[var(--text)]">You have unsaved arrangement changes.</span>
                                <span className="text-[var(--muted)] hidden sm:inline">Click "Save Arrangement" to update your live brand page product display.</span>
                            </div>
                            <button
                                onClick={handleSaveOrder}
                                disabled={saving}
                                className="font-bold underline text-[var(--accent)] hover:opacity-80 cursor-pointer"
                            >
                                Save Now
                            </button>
                        </div>
                    )}

                    {/* Filter Controls Toolbar */}
                    <div className="mt-5 pt-5 border-t border-[var(--border)] flex flex-col md:flex-row items-center justify-between gap-3">
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                            {/* Search Input */}
                            <div className="relative w-full sm:w-64">
                                <RiSearch2Line className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search by name / ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 text-xs md:text-sm bg-[var(--surface)] border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--muted)] hover:text-[var(--text)]"
                                    >
                                        <RiCloseLine className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Product Type Filter */}
                            <div className="relative w-full sm:w-48">
                                <RiPriceTag3Line className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] w-4 h-4 pointer-events-none" />
                                <select
                                    value={selectedType}
                                    onChange={(e) => setSelectedType(e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 text-xs md:text-sm bg-[var(--surface)] border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:border-[var(--accent)] transition-colors appearance-none cursor-pointer capitalize font-medium"
                                >
                                    <option value="">All Product Types</option>
                                    {availableTypes.map((t) => (
                                        <option key={t} value={t} className="capitalize bg-[var(--surface)] text-[var(--text)]">
                                            {t === 'mcombo' ? 'Make Combo' : t}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Category Filter */}
                            <div className="relative w-full sm:w-52">
                                <RiFilter3Line className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] w-4 h-4 pointer-events-none" />
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    className="w-full pl-9 pr-8 py-2 text-xs md:text-sm bg-[var(--surface)] border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:border-[var(--accent)] transition-colors appearance-none cursor-pointer font-medium"
                                >
                                    <option value="">All Categories ({categories.length})</option>
                                    {categories.map((c) => (
                                        <option key={c.categoryID ?? c.id} value={c.categoryID ?? c.id} className="bg-[var(--surface)] text-[var(--text)]">
                                            {c.categoryName ?? c.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Clear All Filters Button */}
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedCategory('');
                                        setSelectedType('');
                                    }}
                                    className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-rose-500 bg-rose-500/10 border border-rose-500/30 rounded-xl hover:bg-rose-500/20 transition-colors"
                                >
                                    <RiCloseLine className="w-4 h-4" /> Clear Filters
                                </button>
                            )}
                        </div>

                        {/* View Switcher & Counter */}
                        <div className="flex items-center justify-between w-full md:w-auto gap-4">
                            <span className="text-xs md:text-sm text-[var(--muted)] font-medium">
                                Showing <strong className="text-[var(--accent)]">{filteredProducts.length}</strong> of{' '}
                                <strong className="text-[var(--text)]">{products.length}</strong> total products
                            </span>

                            <div className="flex items-center bg-[var(--surface)] p-1 rounded-xl border border-[var(--border)]">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 rounded-lg text-xs md:text-sm transition-all ${
                                        viewMode === 'grid'
                                            ? 'bg-[var(--accent)] text-[var(--accent-contrast)] shadow-sm font-semibold'
                                            : 'text-[var(--muted)] hover:text-[var(--text)]'
                                    }`}
                                    title="Visual Grid View"
                                >
                                    <RiLayoutGridLine className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    className={`p-1.5 rounded-lg text-xs md:text-sm transition-all ${
                                        viewMode === 'list'
                                            ? 'bg-[var(--accent)] text-[var(--accent-contrast)] shadow-sm font-semibold'
                                            : 'text-[var(--muted)] hover:text-[var(--text)]'
                                    }`}
                                    title="Compact Table List View"
                                >
                                    <RiListUnordered className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Instructions Banner */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 bg-[var(--surface)] rounded-xl border border-[var(--border)] text-xs text-[var(--muted)] shadow-xs">
                    <div className="flex items-center gap-2">
                        <RiInformationLine className="w-4 h-4 text-[var(--accent)] flex-shrink-0" />
                        <span>
                            <strong className="text-[var(--text)]">Reorder Tip:</strong> Drag and drop products to rearrange their positions, or type the desired rank number in the box and press Enter. Click <strong>Save Arrangement</strong> when done.
                        </span>
                    </div>
                    {hasActiveFilters && (
                        <span className="bg-purple-500/10 text-purple-400 border border-purple-500/30 px-2.5 py-1 rounded-md font-semibold text-[11px] flex-shrink-0">
                            Filtered Mode Active ({filteredProducts.length} visible)
                        </span>
                    )}
                </div>

                {/* Content Area */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-24 bg-[var(--surface)] rounded-2xl border border-[var(--border)] shadow-sm">
                        <div className="w-10 h-10 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                        <p className="text-sm text-[var(--muted)] font-medium">Loading your brand products...</p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-[var(--surface)] rounded-2xl border border-[var(--border)] shadow-sm text-center">
                        <RiShoppingBag3Line className="w-12 h-12 text-[var(--muted)] opacity-50 mb-3" />
                        <h3 className="text-base font-bold text-[var(--text)]">No products found</h3>
                        <p className="text-xs text-[var(--muted)] max-w-sm mt-1">
                            {hasActiveFilters ? 'Try adjusting your search or category filter.' : 'Upload products to begin arranging them.'}
                        </p>
                        {hasActiveFilters && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('');
                                    setSelectedType('');
                                }}
                                className="mt-4 px-4 py-1.5 text-xs font-semibold text-[var(--accent)] bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent)] rounded-lg transition-colors"
                            >
                                Reset All Filters
                            </button>
                        )}
                    </div>
                ) : viewMode === 'grid' ? (
                    /* ─────────────────────────── GRID VIEW ─────────────────────────── */
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {filteredProducts.map((product, filterIndex) => {
                            const masterIndex = products.findIndex((p) => p.productID === product.productID);
                            const masterRank = masterIndex + 1;
                            const filterRank = filterIndex + 1;

                            const isBeingDragged = draggedIndex === masterIndex;
                            const isDropTarget = dragOverIndex === masterIndex;
                            const imageUrl = getProductImageUrl(product.featuredImage);

                            return (
                                <div
                                    key={product.productID}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, product.productID)}
                                    onDragOver={(e) => handleDragOver(e, product.productID)}
                                    onDrop={(e) => handleDrop(e, product.productID)}
                                    onDragEnd={handleDragEnd}
                                    className={`group relative bg-[var(--surface)] rounded-2xl border transition-all duration-200 overflow-hidden flex flex-col select-none cursor-grab active:cursor-grabbing ${
                                        isBeingDragged
                                            ? 'opacity-30 border-dashed border-[var(--accent)] scale-95 shadow-none'
                                            : isDropTarget
                                            ? 'border-[var(--accent)] ring-2 ring-[var(--accent)] bg-[var(--accent)]/5 shadow-lg scale-102'
                                            : 'border-[var(--border)] hover:border-[var(--accent)] hover:shadow-md'
                                    }`}
                                >
                                    {/* Top Card Bar: Badges + Drag Handle */}
                                    <div className="p-2.5 pb-0 flex items-center justify-between gap-1 z-10">
                                        <div className="flex items-center gap-1 flex-wrap">
                                            <span
                                                className={`px-2 py-0.5 rounded-md text-xs font-bold font-mono ${
                                                    masterRank <= 3
                                                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                                                        : masterRank <= 10
                                                        ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                                        : 'bg-[var(--border)] text-[var(--muted)] border border-[var(--border)]'
                                                }`}
                                                title={`Catalog Rank #${masterRank}`}
                                            >
                                                #{masterRank}
                                            </span>
                                            {hasActiveFilters && (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20" title={`Filtered Position ${filterRank}`}>
                                                    Pos {filterRank}
                                                </span>
                                            )}
                                        </div>

                                        <div className="text-[var(--muted)] group-hover:text-[var(--accent)] transition-colors p-1">
                                            <RiDragMove2Fill className="w-4 h-4" />
                                        </div>
                                    </div>

                                    {/* Product Image Thumbnail */}
                                    <div className="px-3 pt-2">
                                        <div className="relative aspect-square w-full rounded-xl bg-[var(--bg)] border border-[var(--border)] overflow-hidden flex items-center justify-center">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover pointer-events-none"
                                                    onError={(e) => {
                                                        if (!e.target.dataset.error) {
                                                            e.target.dataset.error = 'true';
                                                            e.target.src = 'https://placehold.co/150x150?text=No+Image';
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-[var(--muted)] pointer-events-none">
                                                    <RiImageLine className="w-8 h-8 opacity-40" />
                                                </div>
                                            )}
                                            {/* Type Tag Overlay */}
                                            {product.type && (
                                                <span className={`absolute bottom-1 left-1 px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wider border shadow-xs ${getTypeBadgeColor(product.type)}`}>
                                                    {product.type === 'mcombo' ? 'Make Combo' : product.type}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Product Details */}
                                    <div className="p-3 flex-1 flex flex-col justify-between">
                                        <div>
                                            <h4
                                                className="text-xs font-bold text-[var(--text)] line-clamp-2 leading-snug mb-1"
                                                title={product.name}
                                            >
                                                {product.name}
                                            </h4>
                                            <div className="flex items-center justify-between text-[11px] text-[var(--muted)] mb-1">
                                                <span className="truncate max-w-[80px]" title={product.productID}>
                                                    {product.productID}
                                                </span>
                                                <span className="font-semibold text-[var(--text)]">
                                                    ₹{Number(product.salePrice || product.regularPrice || 0).toLocaleString('en-IN')}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Quick Action Controls */}
                                        <div className="mt-2 pt-2 border-t border-[var(--border)] flex items-center justify-between gap-1">
                                            {/* Pin to Top (#1) */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveToTop(product.productID);
                                                }}
                                                disabled={masterIndex === 0}
                                                className="p-1 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                title="Pin to Top (#1)"
                                            >
                                                <RiPushpin2Line className="w-3.5 h-3.5" />
                                            </button>

                                            {/* Move Up */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveUp(product.productID);
                                                }}
                                                disabled={hasActiveFilters ? filterIndex === 0 : masterIndex === 0}
                                                className="p-1 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                title="Move Up 1 Position"
                                            >
                                                <RiArrowUpLine className="w-3.5 h-3.5" />
                                            </button>

                                            {/* Move Down */}
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMoveDown(product.productID);
                                                }}
                                                disabled={hasActiveFilters ? filterIndex === filteredProducts.length - 1 : masterIndex === products.length - 1}
                                                className="p-1 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                title="Move Down 1 Position"
                                            >
                                                <RiArrowDownLine className="w-3.5 h-3.5" />
                                            </button>

                                            {/* Position Jump Input */}
                                            <div className="flex items-center gap-0.5">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={hasActiveFilters ? filteredProducts.length : products.length}
                                                    placeholder={String(hasActiveFilters ? filterRank : masterRank)}
                                                    value={
                                                        rankInputValues[product.productID] !== undefined
                                                            ? rankInputValues[product.productID]
                                                            : ''
                                                    }
                                                    onChange={(e) => handleRankInputChange(product.productID, e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleRankInputCommit(product.productID);
                                                        }
                                                    }}
                                                    onBlur={() => handleRankInputCommit(product.productID)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-10 h-6 text-center text-[11px] font-mono font-bold bg-[var(--bg)] border border-[var(--border)] rounded text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
                                                    title={hasActiveFilters ? "Type position in filtered list and hit Enter" : "Type rank and hit Enter"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* ─────────────────────────── LIST / TABLE VIEW ─────────────────────────── */
                    <div className="bg-[var(--surface)] rounded-2xl border border-[var(--border)] overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-[var(--bg)] border-b border-[var(--border)] text-[var(--muted)] text-xs uppercase font-semibold">
                                        <th className="py-3 px-4 w-12 text-center">Grab</th>
                                        <th className="py-3 px-4 w-24">Rank</th>
                                        <th className="py-3 px-4 w-16">Image</th>
                                        <th className="py-3 px-4">Product Name</th>
                                        <th className="py-3 px-4">Type</th>
                                        <th className="py-3 px-4">Price</th>
                                        <th className="py-3 px-4 w-44 text-center">Quick Move</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[var(--border)] text-xs md:text-sm">
                                    {filteredProducts.map((product, filterIndex) => {
                                        const masterIndex = products.findIndex((p) => p.productID === product.productID);
                                        const masterRank = masterIndex + 1;
                                        const filterRank = filterIndex + 1;

                                        const isBeingDragged = draggedIndex === masterIndex;
                                        const isDropTarget = dragOverIndex === masterIndex;
                                        const imageUrl = getProductImageUrl(product.featuredImage);

                                        return (
                                            <tr
                                                key={product.productID}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, product.productID)}
                                                onDragOver={(e) => handleDragOver(e, product.productID)}
                                                onDrop={(e) => handleDrop(e, product.productID)}
                                                onDragEnd={handleDragEnd}
                                                className={`transition-colors cursor-grab active:cursor-grabbing ${
                                                    isBeingDragged
                                                        ? 'opacity-30 bg-[var(--accent)]/10'
                                                        : isDropTarget
                                                        ? 'bg-[var(--accent)]/20'
                                                        : 'hover:bg-[var(--bg)]'
                                                }`}
                                            >
                                                {/* Drag Handle */}
                                                <td className="py-3 px-4 text-center text-[var(--muted)] hover:text-[var(--accent)]">
                                                    <RiDragMove2Fill className="w-5 h-5 mx-auto" />
                                                </td>

                                                {/* Rank Badges */}
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span
                                                            className={`inline-flex items-center justify-center px-2 py-0.5 rounded-lg text-xs font-bold font-mono ${
                                                                masterRank <= 3
                                                                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                                                                    : masterRank <= 10
                                                                    ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                                                    : 'bg-[var(--border)] text-[var(--muted)] border border-[var(--border)]'
                                                            }`}
                                                        >
                                                            #{masterRank}
                                                        </span>
                                                        {hasActiveFilters && (
                                                            <span className="text-[10px] text-[var(--muted)]">
                                                                (Pos {filterRank})
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Thumbnail */}
                                                <td className="py-3 px-4">
                                                    <div className="w-12 h-12 rounded-lg bg-[var(--bg)] border border-[var(--border)] overflow-hidden flex items-center justify-center flex-shrink-0">
                                                        {imageUrl ? (
                                                            <img
                                                                src={imageUrl}
                                                                alt={product.name}
                                                                className="w-full h-full object-cover pointer-events-none"
                                                                onError={(e) => {
                                                                    if (!e.target.dataset.error) {
                                                                        e.target.dataset.error = 'true';
                                                                        e.target.src = 'https://placehold.co/100x100?text=No+Img';
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <RiImageLine className="w-5 h-5 text-[var(--muted)] opacity-50" />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Product Name & ID */}
                                                <td className="py-3 px-4">
                                                    <div className="font-semibold text-[var(--text)] line-clamp-1 max-w-md">
                                                        {product.name}
                                                    </div>
                                                    <div className="text-[11px] text-[var(--muted)] font-mono">
                                                        ID: {product.productID}
                                                    </div>
                                                </td>

                                                {/* Type */}
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wider border ${getTypeBadgeColor(product.type)}`}>
                                                        {product.type || 'Single'}
                                                    </span>
                                                </td>

                                                {/* Price */}
                                                <td className="py-3 px-4 font-semibold text-[var(--text)]">
                                                    ₹{Number(product.salePrice || product.regularPrice || 0).toLocaleString('en-IN')}
                                                </td>

                                                {/* Quick Move Actions */}
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center justify-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMoveToTop(product.productID);
                                                            }}
                                                            disabled={masterIndex === 0}
                                                            className="p-1.5 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                            title="Pin to Top (#1)"
                                                        >
                                                            <RiPushpin2Line className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMoveUp(product.productID);
                                                            }}
                                                            disabled={hasActiveFilters ? filterIndex === 0 : masterIndex === 0}
                                                            className="p-1.5 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                            title="Move Up 1 Position"
                                                        >
                                                            <RiArrowUpLine className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMoveDown(product.productID);
                                                            }}
                                                            disabled={hasActiveFilters ? filterIndex === filteredProducts.length - 1 : masterIndex === products.length - 1}
                                                            className="p-1.5 rounded-lg text-[var(--muted)] hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                                                            title="Move Down 1 Position"
                                                        >
                                                            <RiArrowDownLine className="w-4 h-4" />
                                                        </button>

                                                        {/* Numerical Rank Jump */}
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={hasActiveFilters ? filteredProducts.length : products.length}
                                                            placeholder={String(hasActiveFilters ? filterRank : masterRank)}
                                                            value={
                                                                rankInputValues[product.productID] !== undefined
                                                                    ? rankInputValues[product.productID]
                                                                    : ''
                                                            }
                                                            onChange={(e) => handleRankInputChange(product.productID, e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    handleRankInputCommit(product.productID);
                                                                }
                                                            }}
                                                            onBlur={() => handleRankInputCommit(product.productID)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="w-11 h-7 text-center text-xs font-mono font-bold bg-[var(--bg)] border border-[var(--border)] rounded text-[var(--text)] focus:outline-none focus:border-[var(--accent)]"
                                                            title={hasActiveFilters ? "Type position and hit Enter" : "Type rank and hit Enter"}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReorderProducts;
